import { useEffect, useRef, useState } from "react";
import { Alert, DialogActions, DialogContent, DialogTitle, Tooltip } from "@mui/material";
import AcUnitRounded from "@mui/icons-material/AcUnitRounded";
import LockOpenRounded from "@mui/icons-material/LockOpenRounded";
import { useTranslation } from "react-i18next";
import { MonitorButton, MonitorDialog } from "./MonitorControls";
import { api } from "../utils/helpers";
import "./PromotionFreeze.css";

export default function PromotionFreeze({ row, spaceId, canManage, disabled: actionDisabled, retiringPackageIds = [], onStart, onEnd, renderAction, returnFocusRef }) {
  const { t } = useTranslation("freigabe");
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sending = useRef(false);
  const alive = useRef(true);
  const disabled = actionDisabled || !canManage || retiringPackageIds.includes(row.packageId);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const freeze = row.freeze || { frozen: false, pending: false, revision: 0 };
  const label = freeze.pending ? "retry" : freeze.frozen ? "unfreeze" : "freeze";
  const close = () => { if (!sending.current) { setIntent(null); requestAnimationFrame(() => returnFocusRef?.current?.focus()); } };
  const open = () => {
    if (disabled || sending.current) return;
    setError(null);
    setIntent({ spaceId, packageId: row.packageId, frozen: freeze.pending ? freeze.desiredFrozen : !freeze.frozen,
      revision: freeze.revision, requestId: freeze.pending ? freeze.operationId : window.crypto.randomUUID() });
  };
  async function submit() {
    if (!intent || sending.current || disabled) return;
    sending.current = true; setBusy(true); setError(null); onStart?.();
    let confirmed = false;
    try {
      const response = await api("/api/cicd/transport/promotions/freeze", { method: "POST", body: JSON.stringify(intent) });
      if (response?.packageId !== row.packageId || response?.freeze?.frozen !== intent.frozen || response.freeze.pending)
        throw new Error("FREEZE_RESPONSE_INVALID");
      confirmed = true;
      if (alive.current) setIntent(null);
    } catch (failure) {
      if (alive.current) setError(t(`freeze.errors.${failure.code}`, { defaultValue: t("freeze.writeError") }));
    } finally {
      try { await onEnd?.(); } finally {
        sending.current = false;
        if (alive.current) {
          setBusy(false);
          if (confirmed) requestAnimationFrame(() => returnFocusRef?.current?.focus());
        }
      }
    }
  }
  return <>
    {freeze.frozen && <Tooltip title={t(freeze.pending ? "freeze.pending" : "freeze.active")}>
      <span className="promotion-freeze-flag" role="img" aria-label={t(freeze.pending ? "freeze.pending" : "freeze.active")}>
        <AcUnitRounded aria-hidden="true"/>
      </span>
    </Tooltip>}
    {renderAction ? renderAction({ canManage, disabled: disabled || busy, label, frozen: freeze.frozen, open }) : canManage && <MonitorButton className="promotion-freeze-action" startIcon={freeze.frozen ? <LockOpenRounded/> : <AcUnitRounded/>}
      disabled={disabled || busy} aria-haspopup="dialog" aria-label={t(`freeze.${label}Named`, { name: row.packageName || row.packageId })}
      onClick={open}>{t(`freeze.${label}`)}</MonitorButton>}
    {intent && <MonitorDialog maxWidth="sm" aria-labelledby={`freeze-title-${row.packageId}`} onClose={close}>
      <DialogTitle id={`freeze-title-${row.packageId}`}>{t(intent.frozen ? "freeze.freeze" : "freeze.unfreeze")}</DialogTitle>
      <DialogContent>
        <p><strong>{row.packageName || row.packageId}</strong></p>
        {row.packageName && row.packageName !== row.packageId && <p>{row.packageId}</p>}
        <p>{t(intent.frozen ? "freeze.freezeHint" : "freeze.unfreezeHint")}</p>
        <p>{t("freeze.scopeHint")}</p>
        {freeze.pending && <Alert severity="warning">{t("freeze.pending")}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <MonitorButton autoFocus disabled={busy} onClick={close}>{t("topics.cancel")}</MonitorButton>
        <MonitorButton disabled={disabled || busy} onClick={submit}>{t(busy ? "promotion.sending" : freeze.pending ? "freeze.retry" : intent.frozen ? "freeze.freeze" : "freeze.unfreeze")}</MonitorButton>
      </DialogActions>
    </MonitorDialog>}
  </>;
}
